/**
 * OPARA v7.0 - i18n Hook
 * 
 * Provides translation functionality across the app.
 * Supports EN, TR, AR, FR, DE, ES with RTL support for Arabic.
 */

import { useMemo } from 'react';
import { getStoredLanguage } from '../config/languages';

// Import all locale files
import en from '../locales/en.json';
import tr from '../locales/tr.json';
import ar from '../locales/ar.json';
import fr from '../locales/fr.json';
import de from '../locales/de.json';
import es from '../locales/es.json';

const locales = {
  en,
  tr,
  ar,
  fr,
  de,
  es
};

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'auth.login.title')
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Replace placeholders in string
 * e.g., "Hello {name}" with { name: "John" } => "Hello John"
 */
const interpolate = (str, params = {}) => {
  if (!str || typeof str !== 'string') return str;
  
  return Object.keys(params).reduce((result, key) => {
    return result.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]);
  }, str);
};

/**
 * Detect language from URL path
 * /en/* => 'en', /tr/* => 'tr', /ar/* => 'ar', etc.
 * Root level (/) => 'en' (gateway, but default to en for any translations shown)
 */
const detectLanguageFromPath = () => {
  const path = window.location.pathname;
  
  // Check for language prefix
  if (path.startsWith('/en')) return 'en';
  if (path.startsWith('/tr')) return 'tr';
  if (path.startsWith('/ar')) return 'ar';
  if (path.startsWith('/fr')) return 'fr';
  if (path.startsWith('/de')) return 'de';
  if (path.startsWith('/es')) return 'es';
  
  // Default to English for root level (gateway)
  return 'en';
};

/**
 * useTranslation Hook
 * 
 * Usage:
 * const { t, locale, isRTL } = useTranslation();
 * t('auth.login.title') => "Sign In" or "Giriş Yap"
 * t('modal.credit_cost', { credits: 5 }) => "This action will use 5 analysis units."
 */
export const useTranslation = () => {
  // Priority: URL path > stored language > default 'en'
  const locale = detectLanguageFromPath();
  
  const translations = useMemo(() => {
    return locales[locale] || locales.en;
  }, [locale]);

  const t = (key, params = {}) => {
    const value = getNestedValue(translations, key);
    
    if (value === null) {
      // Fallback to English if key not found
      const fallback = getNestedValue(locales.en, key);
      if (fallback) {
        return interpolate(fallback, params);
      }
      // Return key if not found anywhere (helps debug missing translations)
      console.warn(`[i18n] Missing translation: ${key}`);
      return key;
    }
    
    return interpolate(value, params);
  };

  const isRTL = locale === 'ar';

  return {
    t,
    locale,
    isRTL,
    direction: isRTL ? 'rtl' : 'ltr'
  };
};

/**
 * Get translation directly (without hook, for non-component use)
 */
export const getTranslation = (key, params = {}, forcedLocale = null) => {
  const locale = forcedLocale || getStoredLanguage() || 'en';
  const translations = locales[locale] || locales.en;
  
  const value = getNestedValue(translations, key);
  
  if (value === null) {
    const fallback = getNestedValue(locales.en, key);
    return fallback ? interpolate(fallback, params) : key;
  }
  
  return interpolate(value, params);
};

export default useTranslation;
